// framework/client/components/ThemeSelector.js
import { ComponentBase } from "../core/ComponentBase.js";

export class ThemeSelector extends ComponentBase {
    static styles = `
        .theme-selector-button {
            cursor: pointer;
            background-color: var(--bs-btn-bg);
            color: var(--bs-btn-color);
            border: 1px solid var(--bs-btn-border-color);
            border-radius: var(--ab-radius-sm);
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast), box-shadow var(--ab-t-fast);
        }
        .theme-selector-button:hover {
            background-color: var(--bs-btn-hover-bg);
            color: var(--bs-btn-hover-color);
        }
        .theme-selector-button:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }

        .theme-selector-menu {
            min-width: 12rem;
            max-height: 200px;
            overflow-y: auto;
            overflow-x: hidden;
            position: absolute;
            z-index: var(--ab-z-dropdown);
            background-color: var(--bs-dropdown-bg);
            color: var(--bs-dropdown-color);
            border-radius: var(--ab-radius-md);
            box-shadow: var(--ab-shadow-md);
            border: 1px solid var(--bs-border-color);
        }
        .theme-selector-menu .dropdown-item {
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
            cursor: pointer;
            font-size: var(--ab-text-sm);
            padding: var(--ab-btn-padding-sm);
        }
        @media (hover: hover) {
            .theme-selector-menu .dropdown-item:hover {
                background-color: var(--ab-hover-bg);
                color: var(--bs-dropdown-link-hover-color);
            }
        }
        .theme-selector-menu .dropdown-item:focus-visible {
            outline: none;
            background-color: var(--ab-hover-bg);
        }
        .theme-selector-menu .dropdown-item.active {
            background-color: var(--ab-active-bg) !important;
            color: var(--ab-active-color) !important;
            font-weight: var(--ab-weight-medium);
        }

        .theme-selector-modal .modal-content {
            border-radius: var(--ab-radius-lg);
            box-shadow: var(--ab-shadow-xl);
            border: 1px solid var(--bs-border-color);
        }
        .theme-selector-modal .btn-close {
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            z-index: var(--ab-z-modal);
            border-radius: var(--ab-radius-sm);
            transition: background-color var(--ab-t-fast), box-shadow var(--ab-t-fast);
        }
        .theme-selector-modal .btn-close:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }

        .theme-preview {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 0.75rem;
        }
        .theme-preview > label {
            flex: 0 0 140px;
            text-align: right;
            font-size: var(--ab-text-sm);
            color: var(--bs-body-color);
        }
        .color-preview {
            width: 24px;
            height: 24px;
            border-radius: var(--ab-radius-xs);
            border: 1px solid var(--bs-border-color);
        }
        body.theme-menu-open {
            overflow: hidden;
        }

        .theme-selector-modal .modal-dialog {
            max-width: 80vw;
        }
        .theme-selector-modal .modal-body {
            max-height: 70vh;
            overflow-y: auto;
            scrollbar-width: thin;
        }
        .theme-group {
            margin-bottom: 1.25rem;
            padding: 1rem;
            border: 1px solid var(--bs-border-color-subtle);
            border-radius: var(--ab-radius-md);
            background-color: var(--bs-tertiary-bg);
        }
        .theme-group-title {
            font-size: var(--ab-label-size);
            font-weight: var(--ab-label-weight);
            letter-spacing: var(--ab-label-spacing);
            text-transform: uppercase;
            color: var(--ab-label-color);
            margin-bottom: 0.75rem;
        }

        .mode-switcher-container {
            padding: 0.5rem 1rem;
        }
        .mode-switcher-label {
            font-size: var(--ab-label-size);
            font-weight: var(--ab-label-weight);
            letter-spacing: var(--ab-label-spacing);
            text-transform: uppercase;
            color: var(--ab-label-color);
            margin-bottom: 0.5rem;
        }
        .mode-switcher-group .btn {
            flex-grow: 1;
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast), border-color var(--ab-t-fast), box-shadow var(--ab-t-fast);
        }
        .mode-switcher-group .btn:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }
        .mode-switcher-group .btn.active {
            background-color: var(--ab-active-bg);
            color: var(--ab-active-color);
            border-color: var(--ab-active-border);
        }
    `;

    constructor(config) {
        super(config);
        this.themes = [];
        this.customTheme = null;
        this.currentTheme = null;
        this.rootContainer = null;
        this.themeMenuOpen = false;
        this.embeddedThemes = this.getEmbeddedThemes();
        this.dropdownInstance = null;
    }

    static getDefaultConfig() {
        return {
            eventGroup: "themeSelector",
            localStorageKey: "userTheme",
            customThemeKey: "userCustomTheme",
            defaultTheme: null,
            themesCssPath: "/assets/themes.css",
            disableThemes: false, // New option
        };
    }

    async initialize() {
        // --- CORRECTED LOGIC: Always load the CSS file ---
        // The `disableThemes` config only affects UI rendering, not the availability of themes.
        await this.loadThemesCSS();
        // --- END CORRECTION ---

        await this.render();
        this.loadUserTheme();

        // --- START FIX: Listen for OS theme changes ---
        this.osThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.osThemeChangeHandler = this.handleOsThemeChange.bind(this);
        this.osThemeMediaQuery.addEventListener('change', this.osThemeChangeHandler);
        // --- END FIX ---

        // --- START FIX: Listen for Storage events to sync theme across tabs ---
        this.storageEventHandler = this.handleStorageThemeChange.bind(this);
        window.addEventListener('storage', this.storageEventHandler);
        // --- END FIX ---

        // --- START FIX: Listen for same-tab theme changes via custom event ---
        this.themeChangedHandler = this.handleSameTabThemeChange.bind(this);
        window.addEventListener('themeChanged', this.themeChangedHandler);
        // --- END FIX ---

        await super.initialize();
    }

    // --- START FIX: Add handler for OS theme changes ---
    handleOsThemeChange() {
        const storedTheme = this.getStoredThemeSelection();
        if (storedTheme === 'system') {
            this.log.info("OS theme changed, re-applying 'system' theme.");
            this.setTheme('system');
            // Also, update the mode switcher UI if it exists
            if (this.container) {
                const systemButton = this.container.querySelector('button[data-mode="system"]');
                if (systemButton && !systemButton.classList.contains('active')) {
                    this.container.querySelectorAll('.mode-switcher-group .btn').forEach(btn => btn.classList.remove('active'));
                    systemButton.classList.add('active');
                }
            }
        }
    }

    // Add handler for Storage events to sync theme changes from other tabs (e.g. documentation/website)
    handleStorageThemeChange(event) {
        if (event.key === this.config.localStorageKey && event.newValue !== this.currentTheme) {
            this.log.info(`Theme selection updated in another tab/window: '${event.newValue}'. Syncing.`);
            const themeToSet = event.newValue || 'system';
            this.setTheme(themeToSet);
            // Update the mode switcher UI if it exists
            if (this.container) {
                const targetButton = this.container.querySelector(`button[data-mode="${themeToSet}"]`);
                if (targetButton && !targetButton.classList.contains('active')) {
                    this.container.querySelectorAll('.mode-switcher-group .btn').forEach(btn => btn.classList.remove('active'));
                    targetButton.classList.add('active');
                }
            }
        }
    }

    // Add handler for same-tab theme changes via custom event
    handleSameTabThemeChange(event) {
        const themeValue = event.detail?.theme;
        if (themeValue && themeValue !== this.currentTheme) {
            this.log.info(`Theme selection updated in same tab/window: '${themeValue}'. Syncing.`);
            const themeToSet = themeValue;
            this.setTheme(themeToSet);
            // Update the mode switcher UI if it exists
            if (this.container) {
                const targetButton = this.container.querySelector(`button[data-mode="${themeToSet}"]`);
                if (targetButton && !targetButton.classList.contains('active')) {
                    this.container.querySelectorAll('.mode-switcher-group .btn').forEach(btn => btn.classList.remove('active'));
                    targetButton.classList.add('active');
                }
            }
        }
    }
    // --- END FIX ---


    async initAfterLogin() { }

    async loadThemesCSS() {
        try {
            // --- START FIX: Check if the style element already exists ---
            const existingStyleElement = document.getElementById('themes-css');
            if (existingStyleElement) {
                this.parseThemesFromCSS(existingStyleElement.textContent);
                this.log.debug("Themes CSS already loaded, parsed existing definitions.");
                return; // Exit early
            }
            // --- END FIX ---

            const response = await fetch(this.config.themesCssPath);
            if (response.ok) {
                const cssText = await response.text();
                const styleElement = document.createElement('style');
                styleElement.id = 'themes-css'; // Set the ID so we can find it later
                styleElement.textContent = cssText;
                document.head.appendChild(styleElement);
                this.parseThemesFromCSS(cssText);
                this.log.info(`${this.config.themesCssPath} loaded and parsed successfully.`);
            } else {
                this.log.warn(`${this.config.themesCssPath} not found, using embedded themes.`);
                this.useEmbeddedThemes();
            }
        } catch (error) {
            this.log.warn(`Error loading ${this.config.themesCssPath}, using embedded themes.`, error);
            this.useEmbeddedThemes();
        }
        this.applyStyles();
    }

    parseThemesFromCSS(cssText) {
        const themeRegex = /\[data-bs-theme="([^"]+)"\]/g;
        let match;
        const foundThemes = new Set();

        while ((match = themeRegex.exec(cssText)) !== null) {
            const themeValue = match[1];
            if (themeValue !== 'custom' && themeValue !== 'light' && themeValue !== 'dark') {
                foundThemes.add(themeValue);
            }
        }
        this.themes = Array.from(foundThemes).map(themeValue => ({
            name: this.capitalizeFirstLetter(themeValue),
            value: themeValue
        }));
    }


    useEmbeddedThemes() {
        this.themes = [];
        const styleElement = document.createElement('style');
        styleElement.textContent = this.embeddedThemes;
        document.head.appendChild(styleElement);
        this.parseThemesFromCSS(this.embeddedThemes);
    }

    getEmbeddedThemes() {
        return `
            `;
    }
    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    async render() {
        if (this.config.disableThemes) {
            if (!this.container) {
                this.container = this.createModeSwitcher();
            }
            this.appClient.tx.applyTranslations(this.container);
            return this.container;
        }

        if (!this.container) {
            this.container = document.createElement('div');
            this.container.classList.add('theme-selector-container', 'dropdown');

            this.themeButton = document.createElement('button');
            this.themeButton.classList.add('btn', 'dropdown-toggle', 'theme-selector-button');
            this.themeButton.setAttribute('title', 'Select Theme');
            this.themeButton.setAttribute('data-bs-toggle', 'dropdown');
            this.themeButton.setAttribute('aria-expanded', 'false');
            this.themeButton.innerHTML = '<i class="bi bi-palette-fill"></i>';
            this.container.appendChild(this.themeButton);

            this.themeMenu = document.createElement('div');
            this.themeMenu.classList.add('dropdown-menu', 'theme-selector-menu');
            this.container.appendChild(this.themeMenu);

            this.createCustomThemeModal();
            this.dropdownInstance = new bootstrap.Dropdown(this.themeButton);
        }

        this.updateThemeMenu();
        return this.container;
    }

    updateThemeMenu() {
        if (this.config.disableThemes || !this.themeMenu) return;

        this.themeMenu.innerHTML = '';

        const lightOption = { name: 'Light', value: 'light' };
        const darkOption = { name: 'Dark', value: 'dark' };
        this.addMenuItem(lightOption);
        this.addMenuItem(darkOption);

        this.themes.forEach(theme => {
            this.addMenuItem(theme);
        });

        const customMenuItem = document.createElement('a');
        customMenuItem.classList.add('dropdown-item');
        if (this.currentTheme === "custom") {
            customMenuItem.classList.add('active');
        }
        customMenuItem.href = '#';
        customMenuItem.textContent = 'Custom';
        customMenuItem.addEventListener('click', (event) => {
            event.preventDefault();
            this.showCustomThemeModal.bind(this)()
            if (this.themeMenuOpen) {
                this.dropdownInstance.hide();
            }
        });
        this.themeMenu.appendChild(customMenuItem);
        this.appClient.tx.applyTranslations(this.container);
    }

    createModeSwitcher() {
        const currentMode = this.getStoredThemeSelection();
        const container = document.createElement('div');
        container.className = 'mode-switcher-container';
        if (!this.config.hideLabel) {
            const label = document.createElement('div');
            label.className = 'mode-switcher-label';
            label.textContent = 'Appearance';
            label.setAttribute('data-i18n', 'Appearance');
            container.appendChild(label);

        }
        const buttonGroup = document.createElement('div');
        buttonGroup.className = 'btn-group btn-group-sm w-100 mode-switcher-group';
        buttonGroup.setAttribute('role', 'group');
        const modes = [
            { id: 'light', icon: 'bi-sun-fill', title: 'Light' },
            { id: 'dark', icon: 'bi-moon-stars-fill', title: 'Dark' },
            { id: 'system', icon: 'bi-display-fill', title: 'System' }
        ];
        modes.forEach(mode => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = `btn btn-outline-secondary ${currentMode === mode.id ? 'active' : ''}`;
            button.innerHTML = `<i class="bi ${mode.icon}"></i>`;
            button.title = mode.title;
            button.setAttribute('data-i18n-title', mode.title);
            button.setAttribute('data-mode', mode.id); // Add data attribute for lookup during sync
            button.addEventListener('click', () => {
                this.setTheme(mode.id);
                buttonGroup.querySelectorAll('.btn').forEach(btn => btn.classList.remove('active'));
                button.classList.add('active');
            });
            buttonGroup.appendChild(button);
        });
        container.appendChild(buttonGroup);
        return container;
    }

    addMenuItem(theme) {
        const menuItem = document.createElement('a');
        menuItem.classList.add('dropdown-item');
        if (this.currentTheme === theme.value) {
            menuItem.classList.add('active');
        }
        menuItem.href = '#';
        menuItem.textContent = theme.name;
        menuItem.dataset.themeValue = theme.value;
        menuItem.addEventListener('click', (event) => {
            event.preventDefault();
            this.setTheme(theme.value);
            if (this.themeMenuOpen) {
                this.dropdownInstance.hide();
            }
        });
        this.themeMenu.appendChild(menuItem);
    }

    // ... (All custom theme modal methods remain unchanged) ...
    createCustomThemeModal() {
        if (this.customThemeModal) {
            return;
        }

        this.customThemeModal = document.createElement('div');
        this.customThemeModal.classList.add('modal', 'fade', 'theme-selector-modal');

        // --- MODAL HTML (Start) ---
        let modalHtml = `
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Custom Theme</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body">
                <!-- NEW: Base Theme Selection -->
                <div class="mb-3">
                    <label for="base-theme-select" class="form-label">Start from Theme:</label>
                    <select class="form-select" id="base-theme-select">
                        <option value="">None (Start from Scratch)</option>
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>`;

        // Add options for other themes
        this.themes.forEach(theme => {
            modalHtml += `<option value="${theme.value}">${theme.name}</option>`;
        });

        modalHtml += `</select>
                </div>`;


        // --- All CSS Variables (Generated) ---
        const allVariables = [
            // Base Colors
            { group: "Base Colors", vars: ["body-bg", "body-color", "primary", "secondary", "success", "danger", "info", "warning", "light", "dark"] },
            // Additional Colors
            { group: "Additional Colors", vars: ["link-color", "link-hover-color", "heading-color", "secondary-color", "tertiary-color", "emphasis-color", "secondary-bg", "tertiary-bg", "emphasis-bg", "primary-bg-subtle", "primary-text-emphasis", "border-color"] },
            // Add more groups/variables as needed (e.g., for buttons, forms, etc. if you have more specific customizations)
        ];

        allVariables.forEach(group => {
            modalHtml += `<div class="theme-group">
                        <div class="theme-group-title">${group.group}</div>`;
            group.vars.forEach(variable => {
                modalHtml += `
                    <div class="mb-3 theme-preview">
                        <label for="custom-${variable}" class="form-label">${variable.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</label>
                        <input type="color" class="form-control form-control-color" id="custom-${variable}">
                        <div class="color-preview" id="preview-${variable}"></div>
                    </div>`;
            });
            modalHtml += `</div>`;
        });

        //NEW: Textarea to hold css output
        modalHtml += `
            <div class="mb-3">
                <label for="custom-theme-css" class="form-label">Generated CSS:</label>
                <textarea class="form-control" id="custom-theme-css" rows="10" readonly></textarea>
            </div>
        `;

        modalHtml += `
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button type="button" class="btn btn-primary" id="save-custom-theme">Save</button>
                <!-- NEW: Export CSS Button -->
                <button type="button" class="btn btn-info" id="export-custom-theme">Export CSS</button>
              </div>
            </div>
          </div>
        `;
        // --- MODAL HTML (End) ---

        this.customThemeModal.innerHTML = modalHtml;
        document.body.appendChild(this.customThemeModal);
        this.customThemeModalInstance = new bootstrap.Modal(this.customThemeModal);

        const colorInputs = this.customThemeModal.querySelectorAll('input[type="color"]');
        colorInputs.forEach(input => {
            const previewId = `preview-${input.id.replace('custom-', '')}`;
            const previewElement = this.customThemeModal.querySelector(`#${previewId}`);
            input.addEventListener('input', () => {
                previewElement.style.backgroundColor = input.value;
            });
        });

        // NEW: Base Theme Change Handler
        this.customThemeModal.querySelector('#base-theme-select').addEventListener('change', this.loadBaseTheme.bind(this));

        this.customThemeModal.querySelector('#save-custom-theme').addEventListener('click', this.saveCustomTheme.bind(this));
        this.customThemeModal.addEventListener('hidden.bs.modal', () => {
            this.resetColorPreviews();
        });

        // NEW: Export CSS Listener
        this.customThemeModal.querySelector('#export-custom-theme').addEventListener('click', this.exportCustomTheme.bind(this));
    }
    // NEW: Load Base Theme into Modal
    loadBaseTheme(event) {
        const selectedThemeValue = event.target.value;

        if (!selectedThemeValue) {
            // If "None" is selected, reset to default values.
            this.resetCustomThemeInputs();
            return;
        }

        let baseThemeStyles = {};

        if (selectedThemeValue === 'light' || selectedThemeValue === 'dark') {
            // Get the computed styles for the selected base theme (light or dark).
            const tempElement = document.createElement('div');
            tempElement.setAttribute('data-bs-theme', selectedThemeValue);
            document.body.appendChild(tempElement); // Needs to be in the DOM to get computed styles.

            // Get all defined variables
            const allVariables = [
                "body-bg", "body-color", "primary", "secondary", "success", "danger", "info", "warning", "light", "dark",
                "link-color", "link-hover-color", "heading-color", "secondary-color", "tertiary-color", "emphasis-color",
                "secondary-bg", "tertiary-bg", "emphasis-bg", "primary-bg-subtle", "primary-text-emphasis", "border-color"
            ];

            allVariables.forEach(variable => {
                const styleName = `--bs-${variable}`;
                baseThemeStyles[styleName] = this.rgbToHex(getComputedStyle(tempElement).getPropertyValue(styleName).trim());
            });

            tempElement.remove();
        } else {
            // Handle custom themes from the CSS file.
            const themeSelector = `[data-bs-theme="${selectedThemeValue}"]`;
            const styleSheet = document.getElementById('themes-css').sheet;

            // Find the CSS rule that matches the selected theme.
            let themeRule = null;
            for (const rule of styleSheet.cssRules) {
                if (rule.selectorText === themeSelector) {
                    themeRule = rule;
                    break;
                }
            }

            if (themeRule) {
                // Get all defined variables
                const allVariables = [
                    "body-bg", "body-color", "primary", "secondary", "success", "danger", "info", "warning", "light", "dark",
                    "link-color", "link-hover-color", "heading-color", "secondary-color", "tertiary-color", "emphasis-color",
                    "secondary-bg", "tertiary-bg", "emphasis-bg", "primary-bg-subtle", "primary-text-emphasis", "border-color"
                ];

                allVariables.forEach(variable => {
                    const styleName = `--bs-${variable}`;
                    // Directly access the property value from the CSSStyleRule
                    baseThemeStyles[styleName] = this.rgbToHex(themeRule.style.getPropertyValue(styleName).trim());
                });
            }
        }

        // Fill the form with the base theme styles.
        for (const key in baseThemeStyles) {
            if (Object.hasOwn(baseThemeStyles, key)) {
                const inputId = `custom-${key.replace('--bs-', '')}`;
                const input = this.customThemeModal.querySelector(`#${inputId}`);

                if (input) {
                    input.value = baseThemeStyles[key];
                    // Update the preview as well
                    const previewId = `preview-${key.replace('--bs-', '')}`;
                    const previewElement = this.customThemeModal.querySelector(`#${previewId}`);
                    if (previewElement) {
                        previewElement.style.backgroundColor = baseThemeStyles[key];
                    }
                }
            }
        }
    }

    rgbToHex(rgb) {
        if (!rgb) return '#ffffff'; // Default to white if undefined

        const match = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (!match) return rgb; // Return original if not in rgb() format

        function hex(x) {
            return ("0" + parseInt(x).toString(16)).slice(-2);
        }
        return "#" + hex(match[1]) + hex(match[2]) + hex(match[3]);
    }


    resetColorPreviews() {
        const previews = this.customThemeModal.querySelectorAll('.color-preview');
        previews.forEach(preview => {
            preview.style.backgroundColor = '';
        });
    }

    generateThemeCSS() {
        let css = `[data-bs-theme="custom"] {\n`;
        const inputs = this.customThemeModal.querySelectorAll('input[type="color"]');
        inputs.forEach(input => {
            const variableName = `--bs-${input.id.replace('custom-', '')}`;
            css += `  ${variableName}: ${input.value};\n`;
        });
        css += `}\n`;


        return css;

    }


    resetCustomThemeInputs() {
        const colorInputs = this.customThemeModal.querySelectorAll('input[type="color"]');
        colorInputs.forEach(input => {
            input.value = ''; // Clear the input value
            const previewId = `preview-${input.id.replace('custom-', '')}`;
            const previewElement = this.customThemeModal.querySelector(`#${previewId}`);
            if (previewElement) {
                previewElement.style.backgroundColor = ''; // Clear the preview
            }
        });
    }


    showCustomThemeModal() {
        this.resetCustomThemeInputs(); // Reset the form before showing

        const currentCustomTheme = this.customTheme || {};
        // Get all defined variables, we now use this in multiple locations so extract to a const
        const allVariables = [
            "body-bg", "body-color", "primary", "secondary", "success", "danger", "info", "warning", "light", "dark",
            "link-color", "link-hover-color", "heading-color", "secondary-color", "tertiary-color", "emphasis-color",
            "secondary-bg", "tertiary-bg", "emphasis-bg", "primary-bg-subtle", "primary-text-emphasis", "border-color"
        ];

        // Fill the form with stored custom theme values or defaults.
        allVariables.forEach(variable => {
            const styleName = `--bs-${variable}`;
            const inputId = `custom-${variable}`;
            const input = this.customThemeModal.querySelector(`#${inputId}`);

            if (input) {
                // Use custom theme value if available, otherwise, use a default (e.g., from light theme).
                input.value = currentCustomTheme[styleName] || ''; // Set to empty string to use the input's default

                // Trigger input event to update preview
                const event = new Event('input', { bubbles: true });
                input.dispatchEvent(event);
            }
        });


        this.customThemeModalInstance.show();
    }


    saveCustomTheme() {
        this.customTheme = {};
        const inputs = this.customThemeModal.querySelectorAll('input[type="color"]');
        inputs.forEach(input => {
            const variableName = `--bs-${input.id.replace('custom-', '')}`;
            this.customTheme[variableName] = input.value;
        });

        localStorage.setItem(this.config.customThemeKey, JSON.stringify(this.customTheme));
        this.setTheme('custom');
        this.customThemeModalInstance.hide();
    }

    exportCustomTheme() {
        const css = this.generateThemeCSS();
        this.customThemeModal.querySelector('#custom-theme-css').value = css;
    }

    setTheme(themeValue) {
        localStorage.setItem(this.config.localStorageKey, themeValue);
        this.currentTheme = themeValue;

        let themeToApply = themeValue;

        if (themeToApply === 'system') {
            const systemIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            themeToApply = systemIsDark ? 'dark' : 'light';
        }

        if (themeToApply === 'light' || themeToApply === 'dark') {
            const clientThemes = this.appClient.sessionTokenHandler.clientConfig?.themes || {};
            themeToApply = themeToApply === 'dark'
                ? (clientThemes.defaultDark || 'dark')
                : (clientThemes.defaultLight || 'light');
        }

        this.log.info(`Theme selection is '${themeValue}', applying theme: '${themeToApply}'`);

        document.documentElement.removeAttribute('style');

        if (themeToApply === 'custom' && this.customTheme) {
            for (const key in this.customTheme) {
                if (Object.hasOwn(this.customTheme, key)) {
                    document.documentElement.style.setProperty(key, this.customTheme[key]);
                }
            }
        }

        document.documentElement.setAttribute('data-bs-theme', themeToApply);

        this.updateProgressLoaderScheme();
        if (!this.config.disableThemes) {
            this.updateThemeMenu();
        }
        window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: themeValue } }));
    }

    loadUserTheme() {
        const storedCustomTheme = localStorage.getItem(this.config.customThemeKey);
        if (storedCustomTheme) {
            this.customTheme = JSON.parse(storedCustomTheme);
        }

        const storedThemeSelection = this.getStoredThemeSelection();
        this.setTheme(storedThemeSelection);
    }

    getStoredThemeSelection() {
        return localStorage.getItem(this.config.localStorageKey) || 'system';
    }

    updateProgressLoaderScheme() {
        const finalThemeName = document.documentElement.getAttribute('data-bs-theme');
        if (finalThemeName === "light" || finalThemeName === "dark") {
            localStorage.setItem("progressScheme", JSON.stringify({ scheme: finalThemeName }));
        } else {
            const computedStyle = getComputedStyle(document.documentElement);
            const overlayColor = computedStyle.getPropertyValue('--bs-body-bg').trim() || "#ffffff";
            const progressBarColor = computedStyle.getPropertyValue('--bs-primary').trim() || "#007bff";
            const progressScheme = {
                overlayColor,
                backgroundColor: overlayColor,
                progressBarColor,
                scheme: finalThemeName,
            };
            localStorage.setItem("progressScheme", JSON.stringify(progressScheme));
        }
    }

    destroy() {
        // --- START FIX: Remove OS theme change listener ---
        if (this.osThemeMediaQuery && this.osThemeChangeHandler) {
            this.osThemeMediaQuery.removeEventListener('change', this.osThemeChangeHandler);
        }
        // --- END FIX ---

        // --- START FIX: Remove storage event listener ---
        if (this.storageEventHandler) {
            window.removeEventListener('storage', this.storageEventHandler);
        }
        // --- END FIX ---

        // --- START FIX: Remove same-tab theme change listener ---
        if (this.themeChangedHandler) {
            window.removeEventListener('themeChanged', this.themeChangedHandler);
        }
        // --- END FIX ---

        super.destroy();
        if (this.dropdownInstance) {
            this.dropdownInstance.dispose();
            this.dropdownInstance = null;
        }
        if (this.container) this.container.remove();
        if (this.customThemeModal) this.customThemeModal.remove();

        document.body.classList.remove('theme-menu-open');
    }
}